import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { supabase } from '../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import { DISPLAY_FONT, makeThemedStyles, useTheme } from '../v10/theme';
import { Chip, Field, GlassCard, GoldButton } from '../v10/ui';
import { TOOL_NAMES, type Gear, type Sector, type Stage } from '../domain/olmps';
import { FloSnapshot, getFloSnapshot, greenStatus, numaThreeGreens } from '../lib/flo';
import type { RiverModel } from '../lib/flo-weights';
import { ToolHost } from './ToolHost';
import { SpectrumReveal } from './SpectrumReveal';
import { useSharedTodayData } from './TodayDataContext';
import { useSyqiSections, ORDER_SECTIONS, enabledToolsFor, SECTION_TOOLS } from './orderSections';
import { useToolEngagement } from './toolEngagement';
import { dateKeyOf } from './useTodayData';
import { runWeeklyTally } from './spectrum-data';
import { isMonthEnd, isSeasonEnd, knownManualMarks, loadRiverModel, periodSummary } from './dayClose';
import {
  AWARD,
  EMPTY_DAY,
  POINTS_UNIT,
  dayPoints,
  minutesOf,
  wakeOf,
  type DayLedger,
  type GameSettings,
  type PointsLedger,
} from './gameSettings';

// Guided-session wrapper — see docs/OLMPS_SYSTEM.md "The daily loop". A session
// is a user-configurable STACK of widgets (Settings -> Game): morning is
// ORACLE -> NUMA -> KYRS by default (O·R·D), evening is the eveningStack (CLIO
// by default) and then "Close the day". On the week boundary the evening
// session first closes the week (CLIO sweep + reveal) and then opens the next
// (ORACLE readout -> NUMA sizes the week -> picks). Month and season ends get
// the same close-then-open shape, lighter. Each step mounts the real tool (which
// owns its own persistence); only the close writes the ledger.
const DUMMY_SECTOR: Sector = 'H';
const DUMMY_STAGE: Stage = 'T';

type Step =
  | { kind: 'tool'; toolId: string; gear: Gear; label: string }
  | { kind: 'weekClose'; label: string }
  | { kind: 'closeDay'; label: string }
  | { kind: 'period'; period: 'month' | 'season'; label: string }
  | { kind: 'picks'; label: string };

/** Native gear for a morning-stack tool (first section that lists it). */
function gearOfTool(toolId: string): Gear {
  return ORDER_SECTIONS.find((g) => SECTION_TOOLS[g].includes(toolId)) ?? 'orient';
}

function buildSteps(kind: 'morning' | 'night', game: GameSettings, now: Date): Step[] {
  if (kind === 'morning') {
    return game.morningStack.map((toolId) => ({
      kind: 'tool' as const,
      toolId,
      gear: gearOfTool(toolId),
      label: TOOL_NAMES[toolId] ?? toolId.toUpperCase(),
    }));
  }
  const steps: Step[] = game.eveningStack.map((toolId) => ({
    kind: 'tool' as const,
    toolId,
    gear: 'revise' as Gear,
    label: TOOL_NAMES[toolId] ?? toolId.toUpperCase(),
  }));
  const weekBoundary = now.getDay() === game.weekBoundaryDay;
  if (weekBoundary) steps.push({ kind: 'weekClose', label: 'The week closes' });
  steps.push({ kind: 'closeDay', label: 'Close the day' });
  if (isMonthEnd(now)) steps.push({ kind: 'period', period: 'month', label: 'The month closes' });
  if (isSeasonEnd(now, game.seasonMonths)) steps.push({ kind: 'period', period: 'season', label: 'The season closes' });
  if (weekBoundary) {
    steps.push({ kind: 'tool', toolId: 'oracle', gear: 'orient', label: 'Next week · ORACLE readout' });
    steps.push({ kind: 'tool', toolId: 'numa', gear: 'regulate', label: 'Next week · NUMA sizes the week' });
    steps.push({ kind: 'picks', label: 'Next week · priorities and Principles' });
  }
  return steps;
}

function FloLine({ game }: { game: GameSettings }) {
  const { t } = useTheme();
  const styles = useStyles();
  const { session } = useAuth();
  const userId = session?.user.id;
  const [flo, setFlo] = useState<FloSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const wakeKey = game.morningSessionTime;

  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const snap = await getFloSnapshot(supabase, userId, 28, wakeOf(new Date(), game));
        if (!cancelled) setFlo(snap);
      } catch {
        /* shown as unavailable */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, wakeKey]);

  return (
    <View style={styles.floLine}>
      {loading ? (
        <ActivityIndicator color={t.goldBright} />
      ) : flo ? (
        <Text style={styles.floLineText}>
          Charge {flo.charge.toFixed(1)} · Load {Math.round(flo.load)} · Flow {Math.round(flo.flow.score)}
          {flo.charge < game.greenBands.chargeMin ? ` · Charge below its green band (${game.greenBands.chargeMin}+)` : ''}
        </Text>
      ) : (
        <Text style={styles.floLineText}>FLO unavailable</Text>
      )}
    </View>
  );
}

/** The week closes: tally the finished week, then show the reveal inline. */
function WeekCloseStep({ userId, game, onContinue }: { userId: string | undefined; game: GameSettings; onContinue: () => void }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (userId) {
          // The session may be opened before the boundary clock time; the
          // evening session itself is the boundary, so tally as of its time.
          const at = new Date();
          const evening = minutesOf(game.eveningSessionTime);
          if (at.getHours() * 60 + at.getMinutes() < evening) at.setHours(Math.floor(evening / 60), evening % 60, 0, 0);
          await runWeeklyTally(supabase, userId, at, game);
        }
      } catch {
        /* best-effort — catch-up tally on open covers it */
      }
      if (alive) setReady(true);
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);
  if (!ready) return <ActivityIndicator style={{ marginTop: 40 }} />;
  return <SpectrumReveal embedded userId={userId} onClose={onContinue} />;
}

function PeriodCloseStep({
  period,
  ledger,
  game,
  onOpenTool,
}: {
  period: 'month' | 'season';
  ledger: PointsLedger;
  game: GameSettings;
  onOpenTool: (toolId: string) => void;
}) {
  const styles = useStyles();
  const months = period === 'month' ? 1 : game.seasonMonths;
  const sum = useMemo(() => periodSummary(ledger, new Date(), months), [ledger, months]);
  return (
    <ScrollView contentContainerStyle={styles.checklistWrap}>
      <GlassCard style={styles.checklistCard}>
        <Text style={styles.checklistTitle}>{period === 'month' ? 'The month closes' : 'The season closes'}</Text>
        <Text style={styles.checklistLine}>{sum.orderedDays} ordered days · {sum.closedDays} days closed</Text>
        <Text style={styles.checklistLine}>
          {sum.points} {POINTS_UNIT} earned over {period === 'month' ? 'the month' : `${months} month${months === 1 ? '' : 's'}`}
        </Text>
        <GoldButton
          label={period === 'month' ? "Set next month's focus" : "Set next season's focus"}
          onPress={() => onOpenTool(period === 'month' ? 'oracle' : 'telos')}
          style={{ marginTop: 10 }}
        />
      </GlassCard>
    </ScrollView>
  );
}

function CloseDayStep({
  userId,
  game,
  ledger,
  markDay,
  onFinished,
}: {
  userId: string | undefined;
  game: GameSettings;
  ledger: PointsLedger;
  markDay: (dateKey: string, patch: Partial<DayLedger>) => void;
  onFinished: () => void;
}) {
  const { t } = useTheme();
  const styles = useStyles();
  const data = useSharedTodayData();
  const { sections, loading: sectionsLoading } = useSyqiSections(userId);
  const engagement = useToolEngagement(userId, data);
  const today = dateKeyOf(new Date());
  const existing = ledger[today];

  const [flo, setFlo] = useState<FloSnapshot | null>(null);
  const [river, setRiver] = useState<RiverModel | null>(null);
  const [readingErr, setReadingErr] = useState(false);
  const [marks, setMarks] = useState<string[]>(existing?.manualMarks ?? []);
  const [added, setAdded] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let alive = true;
    Promise.all([getFloSnapshot(supabase, userId, 28, wakeOf(new Date(), game)), loadRiverModel(supabase, userId)])
      .then(([f, r]) => {
        if (!alive) return;
        setFlo(f);
        setRiver(r);
      })
      .catch(() => alive && setReadingErr(true));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // A row is green when every dot in its gear is green (same signal as the SyQi home).
  const rows = ORDER_SECTIONS.map((gear) => {
    const tools = enabledToolsFor(sections, gear);
    return { gear, green: tools.length > 0 && tools.every((id) => engagement.isEngaged(gear, id)) };
  });
  const rowsGreen = rows.filter((r) => r.green).length;

  // NUMA three greens — the river's own totalLoad and baseline for Load (not flo.load).
  const greens = flo && river ? greenStatus({ charge: flo.charge, load: river.totalLoad, flow: flo.flow.score }, game.greenBands, river.baseline) : null;
  const threeGreens = flo && river ? numaThreeGreens({ charge: flo.charge, load: river.totalLoad, flow: flo.flow.score }, game.greenBands, river.baseline) : false;

  const options = useMemo(() => [...new Set([...knownManualMarks(ledger), ...added])], [ledger, added]);
  const toggleMark = (m: string) => setMarks((cur) => (cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m]));
  const addMark = () => {
    const v = draft.trim();
    if (!v) return;
    setAdded((a) => (a.includes(v) ? a : [...a, v]));
    setMarks((m) => (m.includes(v) ? m : [...m, v]));
    setDraft('');
  };

  const preview: DayLedger = { ...EMPTY_DAY, ...existing, rowsGreen, numaThreeGreens: threeGreens, eveningSession: true, manualMarks: marks };
  const points = dayPoints(preview);
  const ready = !sectionsLoading && !engagement.loading && (readingErr || (!!flo && !!river));

  const close = () => {
    markDay(today, { rowsGreen, numaThreeGreens: threeGreens, eveningSession: true, manualMarks: marks });
    setClosed(true);
  };

  const dot = (on: boolean | null) => (
    <View style={[styles.dot, { backgroundColor: on === null ? t.lineFaint : on ? t.onPace : t.lapsed }]} />
  );

  return (
    <ScrollView contentContainerStyle={styles.checklistWrap}>
      <GlassCard style={styles.checklistCard}>
        <Text style={styles.checklistTitle}>Close the day</Text>

        <Text style={styles.sub}>ORDER rows</Text>
        <View style={styles.rowDots}>
          {rows.map((r) => (
            <View key={r.gear} style={styles.rowDotCell}>
              {dot(r.green)}
              <Text style={styles.rowDotLabel}>{r.gear.slice(0, 3).toUpperCase()}</Text>
            </View>
          ))}
        </View>
        <Text style={styles.checklistLine}>
          {rowsGreen} of 5 green · +{Math.min(5, rowsGreen) * AWARD.ORDER_ROW_GREEN} {POINTS_UNIT}
          {rowsGreen === 5 ? ' · an ordered day' : ''}
        </Text>

        <Text style={styles.sub}>NUMA · Charge, Load, Flow</Text>
        {!ready && !readingErr ? (
          <ActivityIndicator color={t.goldBright} />
        ) : greens ? (
          <>
            <View style={styles.rowDots}>
              {[
                { k: 'Charge', on: greens.charge },
                { k: 'Load', on: greens.load },
                { k: 'Flow', on: greens.flow },
              ].map((g) => (
                <View key={g.k} style={styles.rowDotCell}>
                  {dot(g.on)}
                  <Text style={styles.rowDotLabel}>{g.k}</Text>
                </View>
              ))}
            </View>
            <Text style={styles.checklistLine}>
              {threeGreens ? `All three green · +${AWARD.NUMA_THREE_GREENS} ${POINTS_UNIT}` : 'Not all three green today'}
            </Text>
          </>
        ) : (
          <Text style={styles.checklistLine}>NUMA reading unavailable — no points for it tonight.</Text>
        )}

        <Text style={styles.sub}>Done today that no tool tracks</Text>
        <View style={styles.chipWrap}>
          {options.map((m) => (
            <Chip key={m} small label={m} active={marks.includes(m)} onPress={() => toggleMark(m)} />
          ))}
        </View>
        <Field value={draft} onChangeText={setDraft} onSubmitEditing={addMark} placeholder="Add a completion mark…" returnKeyType="done" />
        {draft.trim() ? <GoldButton ghost compact label="Add mark" onPress={addMark} /> : null}

        <Text style={styles.total}>
          Today: {points} {POINTS_UNIT}
          <Text style={styles.totalSub}>
            {'  '}({preview.stepActions} step action{preview.stepActions === 1 ? '' : 's'} · evening +{AWARD.EVENING_SESSION})
          </Text>
        </Text>

        {closed ? (
          <>
            <Text style={styles.checklistRule}>Day closed. Go to bed with nothing out of place.</Text>
            <GoldButton label="Done" onPress={onFinished} />
          </>
        ) : (
          <GoldButton label={existing?.eveningSession ? 'Update the close' : 'Close the day'} onPress={close} disabled={!ready} />
        )}
      </GlassCard>
    </ScrollView>
  );
}

export function SessionFlow({
  kind,
  onDone,
  onOpenTool,
  game,
  ledger,
  markDay,
}: {
  kind: 'morning' | 'night';
  onDone: () => void;
  onOpenTool: (toolId: string) => void;
  /** Settings + ledger from the shell's one useCompassConfig instance. */
  game: GameSettings;
  ledger: PointsLedger;
  markDay: (dateKey: string, patch: Partial<DayLedger>) => void;
}) {
  const styles = useStyles();
  const { session } = useAuth();
  const userId = session?.user.id;
  const [stepIndex, setStepIndex] = useState(0);
  const steps = useMemo(() => buildSteps(kind, game, new Date()), [kind, game]);
  const stepCount = steps.length;
  const step = steps[Math.min(stepIndex, stepCount - 1)];
  const isLast = stepIndex >= stepCount - 1;

  const next = () => {
    if (isLast) onDone();
    else setStepIndex((i) => Math.min(stepCount - 1, i + 1));
  };

  let body: React.ReactNode;
  if (!step) {
    body = null;
  } else if (step.kind === 'tool') {
    const tool = (
      <ToolHost toolId={step.toolId} sector={DUMMY_SECTOR} stage={DUMMY_STAGE} gear={step.gear} onOpenTool={(id) => onOpenTool(id)} />
    );
    body =
      step.toolId === 'numa' ? (
        <View style={styles.numaWrap}>
          <FloLine game={game} />
          <View style={styles.numaBody}>{tool}</View>
        </View>
      ) : (
        tool
      );
  } else if (step.kind === 'weekClose') {
    body = <WeekCloseStep userId={userId} game={game} onContinue={next} />;
  } else if (step.kind === 'closeDay') {
    body = <CloseDayStep userId={userId} game={game} ledger={ledger} markDay={markDay} onFinished={next} />;
  } else if (step.kind === 'period') {
    body = <PeriodCloseStep period={step.period} ledger={ledger} game={game} onOpenTool={onOpenTool} />;
  } else {
    body = (
      <ScrollView contentContainerStyle={styles.checklistWrap}>
        <GlassCard style={styles.checklistCard}>
          <Text style={styles.checklistTitle}>Open the week</Text>
          <Text style={styles.checklistLine}>Pick next week's priorities and the Principles you will practise.</Text>
          <GoldButton label="Choose priorities and Principles" onPress={() => onOpenTool('moyri')} style={{ marginTop: 10 }} />
        </GlassCard>
      </ScrollView>
    );
  }

  return (
    <View style={styles.root}>
      <Pressable style={styles.exitButton} onPress={onDone} hitSlop={10}>
        <Text style={styles.exitButtonText}>✕</Text>
      </Pressable>
      <View style={styles.body}>{body}</View>
      <View style={styles.footer}>
        <Text style={styles.footerLabel}>
          {kind === 'morning' ? 'Morning session' : 'Evening session'} · {step?.label ?? ''} · {Math.min(stepIndex + 1, stepCount)}/{stepCount}
        </Text>
        <Pressable style={styles.footerButton} onPress={next}>
          <Text style={styles.footerButtonText}>{isLast ? 'Finish' : 'Next'}</Text>
        </Pressable>
      </View>
    </View>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    root: { flex: 1 },
    body: { flex: 1 },
    exitButton: {
      position: 'absolute',
      top: 6,
      right: 12,
      zIndex: 10,
      width: 32,
      height: 32,
      borderRadius: 16,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: t.navyRaised,
      borderWidth: 1,
      borderColor: t.navyLine,
    },
    exitButtonText: { fontSize: 15, color: t.ivorySoft, fontWeight: '700' },
    footer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingVertical: 12,
      backgroundColor: t.glassStrong,
      borderTopWidth: 1,
      borderTopColor: t.navyLine,
    },
    footerLabel: { fontSize: 11, fontWeight: '800', color: t.ivoryFaint, letterSpacing: 1.2, textTransform: 'uppercase' },
    footerButton: {
      backgroundColor: t.gold,
      boxShadow: `0 6px 18px ${t.goldGlow}`,
      borderRadius: 999,
      paddingHorizontal: 22,
      paddingVertical: 11,
    },
    footerButtonText: { fontWeight: '800', color: t.onGold, letterSpacing: 0.4 },

    numaWrap: { flex: 1 },
    floLine: {
      paddingHorizontal: 16,
      paddingVertical: 10,
      backgroundColor: t.glass,
      borderBottomWidth: 1,
      borderBottomColor: t.lineFaint,
    },
    floLineText: { fontWeight: '800', color: t.goldBright, fontSize: 13 },
    numaBody: { flex: 1 },

    checklistWrap: { flexGrow: 1, padding: 16, justifyContent: 'center' },
    sub: { marginTop: 8, fontSize: 11, fontWeight: '800', letterSpacing: 1.2, textTransform: 'uppercase', color: t.ivoryFaint },
    rowDots: { flexDirection: 'row', gap: 14, marginTop: 4 },
    rowDotCell: { alignItems: 'center', gap: 3 },
    rowDotLabel: { fontSize: 9.5, fontWeight: '700', letterSpacing: 1, color: t.ivoryFaint },
    dot: { width: 18, height: 18, borderRadius: 9 },
    chipWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
    total: { marginTop: 10, fontSize: 18, fontWeight: '900', color: t.goldBright },
    totalSub: { fontSize: 11.5, fontWeight: '600', color: t.ivorySoft },
    checklistCard: {
      borderRadius: 22,
      padding: 20,
      gap: 10,
    },
    checklistTitle: { fontSize: 24, color: t.ivory, fontFamily: DISPLAY_FONT, letterSpacing: 2.5, textTransform: 'uppercase' },
    checklistLine: { fontSize: 14, fontWeight: '700', color: t.ivorySoft },
    checklistRule: {
      marginTop: 8,
      fontSize: 13,
      fontStyle: 'italic',
      color: t.ivorySoft,
      borderLeftWidth: 2,
      borderLeftColor: t.gold,
      paddingLeft: 10,
    },
  }),
);
